"use client";
import { ChatWindow } from "components/ChatWindow";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { useUserLocationInfo, IUserLocationInfo } from "../../scripts/hooks/useUserLocationInfo";
import { trackEvent, matomoUserId } from "services/api-client/matomoTracker";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";
import { usePreservedRouteKey } from "scripts/hooks/usePreservedRouteKey";
import { ShowNotFoundOrUnAuthorizedUiBasedOnStatusCode } from "components/ShowNotFoundOrUnAuthorizedUiBasedOnStatusCode";
import { IUser } from "services/UserContext";
import { IAssistant, IChatSession } from "layouts/DefaultLayout";
import ErrorBoundary from "components/ErrorBoundary";
import ChatErrorFallback from "components/ChatErrorFallback";
import { MetaHead } from "components/MetaHead";
import { GuestLanding } from "components/GuestLanding";
import { SidebarSignUp } from "components/SidebarSignUp";
import { isSupportChatAvailable } from "utils/supportChat";
import { SupportChatIcon } from "components/SupportChat";

export interface IQuestion {
  title: string;
  message: string;
  icon?: string;
}

interface OutletContext {
  user?: IUser;
  setAssistant(assistant: IAssistant.Input): void;
  onNewAssistantSession(date: string, session: IChatSession): void;
  statusCode: number;
  blockedByGuestAccess: boolean;
}

export default function SearchChatbotPage() {
  const { isContentNotVisible, initialQuestion, hasListingFilter, embed } = useQueryFlags();
  const { session } = useParams();
  const chatKey = usePreservedRouteKey();
  const shouldFetchLocation = !initialQuestion && !hasListingFilter && !session;

  const { zipCode, data: location } = useUserLocationInfo({
    fetchOnMount: shouldFetchLocation,
  });

  const {
    user,
    setAssistant,
    onNewAssistantSession,
    statusCode,
    blockedByGuestAccess
  } = useOutletContext<OutletContext>();

  // The layout owns the assistant, but this page needs it while rendering (it
  // feeds ChatWindow and the sidebar), so build it here and hand it to the
  // layout from an effect. Calling the layout's setter during this component's
  // render is what triggers React's "Cannot update a component while rendering
  // a different component" warning. useLayoutEffect keeps the layout in sync
  // before the browser paints, so the sidebar never flashes empty.
  const assistant = useMemo<IAssistant>(
    () => ({ id: 'search', type: 'search', name: 'search', sessionId: session }),
    [session]
  );

  useLayoutEffect(() => {
    setAssistant(assistant);
  }, [assistant, setAssistant]);

  const hasTrackedVisit = useRef(false);
  useEffect(() => {
    if (!user?.user_id || hasTrackedVisit.current) return;
    hasTrackedVisit.current = true;
    trackEvent("AI Mode / Home Search Visit", { uid: matomoUserId(user) });
  }, [user?.user_id, user?.username]);

  const questions: IQuestion[] = [
    { title: 'Your dream home, just listed.', message: `Show me just listed properties in ${zipCode}`, icon: 'https://content.harstatic.com/media/icons/filters/icon_just_listed.svg' },
    { title: 'Homes with recent price reductions.', message: `Show me properties with a price reduction in ${zipCode}`, icon: 'https://content.harstatic.com/media/icons/filters/icon_price_reduced.svg' },
    { title: 'Modern living in new construction.', message: `Show me new construction properties in ${zipCode}`, icon: 'https://content.harstatic.com/media/icons/filters/icon_new_construction.svg' },
    { title: 'Tour an open house today.', message: `Show me open houses in ${zipCode}`, icon: 'https://content.harstatic.com/media/icons/filters/icon_open_house.svg' }
  ];

  const Title = (
    <span>
      <div className="titlesection_title">
        <svg width="76" height="33" viewBox="0 0 76 33" xmlns="http://www.w3.org/2000/svg" className="beta-badge">
          <g fill="none" fillRule="evenodd">
            <rect fill="#FD4B78" width="76" height="33" rx="16.5" />
            <text fontFamily="Inter-Regular_Bold, Inter" fontSize="22.846" fontWeight="bold" fill="#FFF">
              <tspan x="9.9" y="25">BETA</tspan>
            </text>
          </g>
        </svg>
        <img src="https://content.harstatic.com/media/icons/gifs/ai-mode.gif" alt="AI Mode" width={350} />
      </div>
      <div className="titlesection_tagline">AI-Powered Conversational Search</div>
    </span>
  )

  const Template = useCallback((props: ChatWindow.ICustomTemplateProps) => {
    const { user, isEmpty, Content, Title, Messages, InputBar, onMessage, showLimitExceedError = false } = props;
    const disabled = showLimitExceedError;
    const isAgent = (user?.role_id ?? 0) > 1;

    if ([401, 403, 404].includes(statusCode)) {
      return <ShowNotFoundOrUnAuthorizedUiBasedOnStatusCode statusCode={statusCode} />
    }

    return (
      <div
        className={isEmpty ? 'new_chat' : 'cpr_container_inner'}
        style={isEmpty ? { backgroundImage: `url(${process.env.PUBLIC_URL}/static/images/ai-mode-background.png)` } : undefined}
      >
        <Content className={isEmpty ? 'start_content' : ''}>
          {isEmpty && (
            <>
              <Title />
              {/* Defer only the guest landing until auth resolves so
                  authenticated visitors never flash it; the notice can render
                  immediately (including the GUEST_USER_ALLOWED=1 path). */}
              {blockedByGuestAccess ? (
                user?.loading !== false ? null : <GuestLanding user={user} />
              ) : (
                <div className="start_notice">
                  <img className="start_notice_icon" src="https://content.harstatic.com/media/icons/a_all/chat_ai_icon2.svg" alt="" />
                  <p className="start_notice_text font_size--medium mb-0">
                    {/* Explicit space: JSX drops the whitespace between the period
                    and the branch below, because it contains a newline. */}
                    {isAgent && <><strong style={{ lineHeight: '1.6em' }}>Dear HAR Member</strong><br /></>}AI Mode is designed for property searches and listing-specific questions.{" "}
                    {isAgent ?

                      // Message for Agents
                      <>
                        For process or contract questions, please contact your broker.
                        {/* Same predicate the launcher itself uses, so this sentence
                      can never point at a button that is not on screen. */}
                        {isSupportChatAvailable(user, embed) && <> For membership, account or technical assistance, select <span className="font_weight--bold d-inline-block text-nowrap"><SupportChatIcon size={21} /> Support</span> to chat with a Member Services representative.</>}
                      </> :

                      // Message for Consumers
                      <>For questions about the buying, selling or renting process, contracts or other real estate matters, please contact a real estate professional.</>
                    }
                  </p>
                </div>
              )}
            </>
          )}
          <Messages />
        </Content>
        <div className="d-flex flex-column cpr_content_shlf">
          {isContentNotVisible || blockedByGuestAccess ? null : (
            <>
              <div className="order-md-0 order-0"><InputBar /></div>
              <div className="order-md-1 order-1">
                {isEmpty && <div className="start_content mb-4 mb-md-0">
                  <div className="start_cards ml-auto mr-auto pt-3">
                    {questions.map((question, index) => (
                      <button
                        type="button"
                        key={index}
                        className={`start_card ${disabled ? "cursor--default" : "cursor--pointer"}`}
                        title={question?.message}
                        onClick={() => !disabled && onMessage(question.message)}
                        disabled={disabled}
                      >
                        {/* <img className="start_card_icon" src={question.icon} alt="" /> */}
                        <span className="start_card_title">{question.title}</span>
                        <img className="start_card_arrow" src="https://content.harstatic.com/media/icons/arrow-right.svg" alt="" />
                      </button>
                    ))}
                  </div>
                </div>}
              </div>
            </>
          )}
          {/* Blocked guests viewing an existing conversation still need a way to
              authenticate; the empty-state GuestLanding does not cover them. */}
          {blockedByGuestAccess && !isEmpty && user?.loading === false && (
            <div className="cpr_content_inner" style={{ width: '100%' }}>
              <SidebarSignUp user={user} assistant={assistant} />
            </div>
          )}
        </div>
      </div>
    );
  }, [assistant, isContentNotVisible, zipCode, statusCode, user, embed]);

  return (
    <ErrorBoundary
      fallback={(error: Error) => (
        <ChatErrorFallback
          error={error}
          title="Search Chat Error"
          message="We encountered an issue while loading the property search chat. Please try again or start a new search."
        />
      )}
    >
      <MetaHead />
      <ChatWindow
        title={Title}
        // title={<span><div className="titlesection_title">AI Mode</div><div className="titlesection_tagline">AI Powered Home Search by <a href={websiteHost} target="_blank" rel="noopener noreferrer">HAR.com</a></div> </span>}
        user={user}
        assistant={assistant}
        Template={Template}
        onNewAssistantSession={onNewAssistantSession}
        location={location as IUserLocationInfo}
        key={chatKey}
      />
    </ErrorBoundary>
  );
}
